import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import {
  CreditCard, Check, AlertTriangle, MessageCircle, Send,
  UploadCloud, RefreshCw, Radio, FileSpreadsheet,
  CheckCircle2, XCircle, Search, UserCheck, UserX, Loader2, Sparkles
} from 'lucide-react';
import { listStudents } from '../services/firebase/studentsService';
import { listClasses } from '../services/firebase/academicService';
import {
  getStudentAttendance,
  saveStudentAttendance,
  recordStudentRfidPunch,
  processBiometricLogsForStudents,
} from '../services/firebase/studentAttendanceService';
import { CLASS_OPTIONS, SECTION_OPTIONS } from '../lib/pdfUtils';
import { getWhatsAppUrl } from '../lib/utils';
import { toast } from 'sonner';

export default function RfidAttendance() {
  const [activeTab, setActiveTab] = useState('tap'); // 'tap' | 'biometric' | 'file' | 'register'
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Date selection
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  // Class & Section filters
  const [cls, setCls] = useState('');
  const [sec, setSec] = useState('A');

  // Live Tap Station State
  const [scanInput, setScanInput] = useState('');
  const [lastScannedStudent, setLastScannedStudent] = useState(null);
  const [lastScannedTime, setLastScannedTime] = useState('');
  const [liveScanFeed, setLiveScanFeed] = useState([]);
  const [scanning, setScanning] = useState(false);
  const scanInputRef = useRef(null);

  // Biometric Sync State
  const [syncingBiometric, setSyncingBiometric] = useState(false);
  const [biometricSummary, setBiometricSummary] = useState(null);

  // File Upload State
  const [fileParsed, setFileParsed] = useState([]);
  const [committing, setCommitting] = useState(false);

  // Class Register State
  const [classAttendance, setClassAttendance] = useState({ records: {}, rfidLogs: {} });
  const [loadingRegister, setLoadingRegister] = useState(false);

  // Load classes and students on mount
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [stuList, clsList] = await Promise.all([
        listStudents({ status: 'ACTIVE' }),
        listClasses(),
      ]);
      setStudents(stuList);
      setClasses(clsList);
      if (clsList.length > 0 && !cls) {
        setCls(clsList[0].name || '1st');
      }
    } catch (e) {
      toast.error('Failed to load students');
    }
    setLoading(false);
  }, [cls]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load class attendance register whenever class, section, or date changes
  const loadRegister = useCallback(async () => {
    if (!cls) return;
    setLoadingRegister(true);
    try {
      const data = await getStudentAttendance(cls, sec, date);
      setClassAttendance(data);
    } catch (e) {
      console.error('Error loading attendance register:', e);
    }
    setLoadingRegister(false);
  }, [cls, sec, date]);

  useEffect(() => {
    loadRegister();
  }, [loadRegister]);

  // Students in selected class and section
  const currentClassStudents = useMemo(() => {
    if (!cls) return [];
    return students.filter(
      (s) => s.className?.toLowerCase() === cls.toLowerCase() &&
             (!sec || (s.section || 'A').toUpperCase() === sec.toUpperCase())
    );
  }, [students, cls, sec]);

  // Handle Live RFID Card Tap / Keystroke Scan
  const handleScanSubmit = async (e) => {
    e?.preventDefault();
    const rfidToProcess = scanInput.trim();
    if (!rfidToProcess) return;

    setScanning(true);
    setScanInput('');

    try {
      const result = await recordStudentRfidPunch(
        rfidToProcess,
        students,
        date,
        null,
        'RFID_Scanner'
      );

      if (result.success && result.found) {
        setLastScannedStudent(result.student);
        setLastScannedTime(result.punchTime);
        setLiveScanFeed((prev) => [
          {
            id: Date.now(),
            student: result.student,
            rfid: rfidToProcess,
            time: result.punchTime,
            status: 'PRESENT',
          },
          ...prev.slice(0, 29), // keep latest 30
        ]);

        toast.success(`Marked PRESENT: ${result.student.fullName} (${result.student.className}-${result.student.section})`);
        // Refresh register
        loadRegister();
      } else {
        toast.error(`Unrecognized RFID tag: ${rfidToProcess}`);
        setLiveScanFeed((prev) => [
          {
            id: Date.now(),
            student: null,
            rfid: rfidToProcess,
            time: new Date().toLocaleTimeString(),
            status: 'UNRECOGNIZED',
          },
          ...prev.slice(0, 29),
        ]);
      }
    } catch (err) {
      console.error('Scan error:', err);
      toast.error('Error processing RFID punch');
    } finally {
      setScanning(false);
      // Keep input focused for consecutive card taps
      scanInputRef.current?.focus();
    }
  };

  // Process Biometric Machine punches for students
  const handleSyncBiometric = async () => {
    setSyncingBiometric(true);
    try {
      const res = await processBiometricLogsForStudents(date, students);
      setBiometricSummary(res);
      toast.success(`Processed ${res.totalLogs} logs: ${res.totalPresent} students marked PRESENT, ${res.totalAbsent} ABSENT.`);
      loadRegister();
    } catch (err) {
      console.error(err);
      toast.error('Failed to process biometric punches for students');
    } finally {
      setSyncingBiometric(false);
    }
  };

  // Parse attendance file (CSV)
  const handleFile = (f) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target.result || '');
      const lines = text.trim().split(/\r?\n/).filter(Boolean);
      if (!lines.length) return;

      const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
      const idIdx = headers.findIndex((h) => ['rfid', 'admissionno', 'admission_no', 'id'].includes(h));
      const presIdx = headers.findIndex((h) => ['present', 'status'].includes(h));

      const parsed = lines.slice(1).map((line) => {
        const cells = line.split(',');
        const id = (cells[idIdx] || '').trim();
        const isPresent = presIdx !== -1
          ? ['1', 'yes', 'p', 'present', 'true'].includes(String(cells[presIdx] || '').trim().toLowerCase())
          : true;
        return { id, present: isPresent };
      });

      setFileParsed(parsed);
      toast.success(`Parsed ${parsed.length} rows from CSV`);
    };
    reader.readAsText(f);
  };

  // Commit File Upload Attendance
  const handleCommitFile = async () => {
    setCommitting(true);
    try {
      const enriched = fileParsed.map((row) => {
        const cleanRowId = String(row.id).trim().replace(/^0+/, '');
        const student = students.find((s) => {
          const s1 = String(s.rfid || '').trim().replace(/^0+/, '');
          const s2 = String(s.rfidNo || '').trim().replace(/^0+/, '');
          return s.admissionNo === row.id || s1 === cleanRowId || s2 === cleanRowId;
        });
        return { ...row, student };
      });

      const matchedRows = enriched.filter((r) => r.student);
      const grouped = {};

      matchedRows.forEach((r) => {
        const c = r.student.className || 'Unknown';
        const s = r.student.section || 'A';
        const key = `${c}_${s}`;
        if (!grouped[key]) {
          grouped[key] = { className: c, section: s, records: {}, rfidLogs: {} };
        }
        grouped[key].records[r.student.id] = r.present ? 'PRESENT' : 'ABSENT';
        if (r.present) {
          grouped[key].rfidLogs[r.student.id] = {
            rfid: r.student.rfid || r.id,
            punchTime: 'Bulk_CSV',
            source: 'CSV_Upload',
          };
        }
      });

      for (const g of Object.values(grouped)) {
        await saveStudentAttendance(g.className, g.section, date, g.records, g.rfidLogs, 'CSV_Upload');
      }

      toast.success(`Saved attendance for ${matchedRows.length} matched students!`);
      loadRegister();
    } catch (err) {
      console.error(err);
      toast.error('Failed to commit attendance');
    } finally {
      setCommitting(false);
    }
  };

  // Toggle single student status in Class Register view
  const handleToggleStudentStatus = async (studentId, currentStatus) => {
    const nextStatus = currentStatus === 'PRESENT' ? 'ABSENT' : 'PRESENT';
    const updatedRecords = { ...(classAttendance.records || {}) };
    updatedRecords[studentId] = nextStatus;

    const updatedRfidLogs = { ...(classAttendance.rfidLogs || {}) };
    if (nextStatus === 'PRESENT' && !updatedRfidLogs[studentId]) {
      const student = students.find((s) => s.id === studentId);
      updatedRfidLogs[studentId] = {
        rfid: student?.rfid || 'Manual',
        punchTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        source: 'Manual_Toggle',
      };
    }

    setClassAttendance((prev) => ({
      ...prev,
      records: updatedRecords,
      rfidLogs: updatedRfidLogs,
      present: Object.values(updatedRecords).filter((v) => v === 'PRESENT').length,
      absent: Object.values(updatedRecords).filter((v) => v === 'ABSENT').length,
    }));

    await saveStudentAttendance(cls, sec, date, updatedRecords, updatedRfidLogs, 'Manual_Edit');
  };

  // Calculate register stats
  const registerStats = useMemo(() => {
    let pres = 0;
    let abs = 0;
    const records = classAttendance.records || {};

    currentClassStudents.forEach((s) => {
      const status = records[s.id] || 'ABSENT';
      if (status === 'PRESENT') pres++;
      else abs++;
    });

    const total = currentClassStudents.length;
    const rate = total > 0 ? Math.round((pres / total) * 100) : 0;

    return { pres, abs, total, rate };
  }, [currentClassStudents, classAttendance.records]);

  // List of absentees for WhatsApp
  const absentees = useMemo(() => {
    const records = classAttendance.records || {};
    return currentClassStudents.filter((s) => (records[s.id] || 'ABSENT') === 'ABSENT');
  }, [currentClassStudents, classAttendance.records]);

  const sendBulkWhatsApp = () => {
    if (!absentees.length) return toast('No absentees to notify');
    absentees.forEach((s) => {
      const phone = s.fatherPhone || s.phoneNumber;
      if (phone) {
        const url = getWhatsAppUrl(
          phone,
          `Dear Parent, your child ${s.fullName} (Adm: ${s.admissionNo}) was marked ABSENT at St. Paul's High School on ${date}. Please contact the school if this is an error.`
        );
        window.open(url, '_blank');
      }
    });
    toast.success(`Opened WhatsApp links for ${absentees.length} absentees`);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-24" data-testid="rfid-attendance">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <NavLink to="/dashboard/attendance" className="label-eyebrow text-primary">← Back to Attendance</NavLink>
          <h1 className="font-display font-black text-3xl tracking-tighter uppercase flex items-center gap-3 mt-1">
            <Radio className="h-7 w-7 text-indigo-500 animate-pulse" />
            Student RFID Attendance Hub
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            RFID card scanning, biometric machine sync, and binary Present/Absent student attendance tracking.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div>
            <label className="label-eyebrow text-muted-foreground block text-[10px]">Attendance Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-10 px-3 rounded-2xl border border-border bg-card text-xs font-bold"
              data-testid="rfid-date"
            />
          </div>
        </div>
      </div>

      {/* Mode Selector Tabs */}
      <div className="flex bg-muted/70 p-1.5 rounded-[2rem] gap-2 overflow-x-auto thin-scrollbar">
        {[
          { k: 'tap', label: '📇 Live RFID Tap Station', sub: 'Instant Card Tap' },
          { k: 'biometric', label: '🔄 Biometric Machine Sync', sub: 'Process Machine Punches' },
          { k: 'register', label: '📋 Class Attendance Register', sub: 'Class-Wise Review & WhatsApp' },
          { k: 'file', label: '📁 CSV File Upload', sub: 'Bulk Attendance File' },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => {
              setActiveTab(t.k);
              if (t.k === 'tap') setTimeout(() => scanInputRef.current?.focus(), 150);
            }}
            className={`flex-1 min-w-[200px] py-2.5 px-4 rounded-[1.5rem] text-left transition-all ${
              activeTab === t.k
                ? 'bg-card text-foreground shadow-md font-bold'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="text-xs font-bold">{t.label}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{t.sub}</div>
          </button>
        ))}
      </div>

      {/* ────────────────────────────────────────────────────────────────────────
          TAB 1: LIVE RFID TAP STATION
      ────────────────────────────────────────────────────────────────────────── */}
      {activeTab === 'tap' && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Scanner Input & Live Tap Result */}
            <div className="lg:col-span-7 space-y-4">
              <div className="glass-morphism rounded-[2.5rem] p-6 border-2 border-indigo-500/30 bg-gradient-to-br from-indigo-500/5 via-background to-background">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full bg-emerald-500 animate-ping" />
                    <span className="font-bold text-sm">Scanner Ready for Card Taps</span>
                  </div>
                  <span className="label-eyebrow text-xs bg-indigo-500/10 text-indigo-600 px-3 py-1 rounded-full font-bold">
                    Date: {date}
                  </span>
                </div>

                <form onSubmit={handleScanSubmit} className="space-y-3">
                  <div className="relative">
                    <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground" />
                    <input
                      ref={scanInputRef}
                      type="text"
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      placeholder="Tap student RFID card or enter RFID number..."
                      autoFocus
                      disabled={scanning}
                      className="w-full h-16 pl-14 pr-28 rounded-2xl border-2 border-border focus:border-indigo-500 bg-card text-lg font-mono tracking-wider outline-none shadow-inner transition-all"
                    />
                    <button
                      type="submit"
                      disabled={scanning || !scanInput.trim()}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 h-11 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs disabled:opacity-50 transition-colors"
                    >
                      {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Record'}
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground italic text-center">
                    💡 Connect any USB RFID reader to your device. Tapping a card will automatically register the student.
                  </p>
                </form>

                {/* Last Scanned Student Preview */}
                <AnimatePresence>
                  {lastScannedStudent && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="mt-6 p-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 flex items-center gap-4"
                    >
                      {lastScannedStudent.photoURL ? (
                        <img
                          src={lastScannedStudent.photoURL}
                          alt=""
                          className="h-16 w-16 rounded-2xl object-cover ring-2 ring-emerald-500/40"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 grid place-items-center text-white font-black text-2xl ring-2 ring-emerald-500/40">
                          {lastScannedStudent.firstName?.[0] || lastScannedStudent.fullName?.[0] || 'S'}
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-black label-eyebrow flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> PRESENT
                          </span>
                          <span className="text-xs text-muted-foreground font-mono">
                            {lastScannedTime}
                          </span>
                        </div>
                        <h3 className="font-display font-black text-xl tracking-tight mt-1 truncate">
                          {lastScannedStudent.fullName}
                        </h3>
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                          <span className="font-bold text-foreground">Class {lastScannedStudent.className}-{lastScannedStudent.section}</span>
                          <span>•</span>
                          <span className="font-mono">Adm: {lastScannedStudent.admissionNo}</span>
                          <span>•</span>
                          <span className="font-mono">RFID: {lastScannedStudent.rfid || lastScannedStudent.rfidNo}</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Quick Status Bar */}
              <div className="grid grid-cols-3 gap-3">
                <div className="glass-morphism rounded-2xl p-4 text-center">
                  <div className="label-eyebrow text-muted-foreground">Total Students</div>
                  <div className="font-display font-black text-2xl tracking-tighter mt-1">{students.length}</div>
                </div>
                <div className="glass-morphism rounded-2xl p-4 text-center border border-emerald-500/20 bg-emerald-500/5">
                  <div className="label-eyebrow text-emerald-500">Scanned Today</div>
                  <div className="font-display font-black text-2xl tracking-tighter mt-1 text-emerald-500">{liveScanFeed.length}</div>
                </div>
                <div className="glass-morphism rounded-2xl p-4 text-center border border-indigo-500/20 bg-indigo-500/5">
                  <div className="label-eyebrow text-indigo-500">Students with RFID</div>
                  <div className="font-display font-black text-2xl tracking-tighter mt-1 text-indigo-500">
                    {students.filter((s) => s.rfid || s.rfidNo).length}
                  </div>
                </div>
              </div>
            </div>

            {/* Live Feed of Today's Scans */}
            <div className="lg:col-span-5 glass-morphism rounded-[2.5rem] p-5 flex flex-col h-[520px]">
              <div className="flex items-center justify-between pb-3 border-b border-border">
                <div className="font-bold text-sm flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-indigo-500" />
                  Live RFID Tap Feed
                </div>
                <span className="text-xs text-muted-foreground font-mono">
                  {liveScanFeed.length} scan(s)
                </span>
              </div>

              <div className="flex-1 overflow-y-auto thin-scrollbar divide-y divide-border/50 mt-2">
                {liveScanFeed.map((item) => (
                  <div key={item.id} className="py-2.5 flex items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-2.5 min-w-0">
                      {item.student ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-rose-500 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        {item.student ? (
                          <>
                            <div className="font-bold text-foreground truncate">{item.student.fullName}</div>
                            <div className="text-[10px] text-muted-foreground font-mono">
                              Class {item.student.className}-{item.student.section} · RFID: {item.rfid}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="font-bold text-rose-500">Unknown Card</div>
                            <div className="text-[10px] text-muted-foreground font-mono">RFID: {item.rfid}</div>
                          </>
                        )}
                      </div>
                    </div>
                    <span className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                      {item.time}
                    </span>
                  </div>
                ))}

                {liveScanFeed.length === 0 && (
                  <div className="py-24 text-center text-xs text-muted-foreground">
                    <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    No cards tapped yet today. Tap a student card to start!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
          TAB 2: BIOMETRIC MACHINE SYNC
      ────────────────────────────────────────────────────────────────────────── */}
      {activeTab === 'biometric' && (
        <div className="glass-morphism rounded-[2.5rem] p-8 max-w-3xl mx-auto space-y-6 text-center">
          <div className="h-16 w-16 rounded-3xl bg-indigo-500/10 text-indigo-500 grid place-items-center mx-auto">
            <Radio className="h-8 w-8" />
          </div>

          <div>
            <h2 className="font-display font-black text-2xl tracking-tight uppercase">
              Process Biometric Machine RFID Punches
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-lg mx-auto">
              Automatically fetch all raw punches recorded by the physical biometric / RFID machines for{' '}
              <span className="font-bold text-foreground">{date}</span>, match student RFID numbers, and record class attendance.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-muted/40 border border-border text-left text-xs max-w-lg mx-auto space-y-2">
            <div className="font-bold flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Simplified Student Attendance Rules:
            </div>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
              <li>Students with an RFID punch on this date are marked <strong className="text-emerald-500">PRESENT</strong>.</li>
              <li>Students without a punch on this date are marked <strong className="text-rose-500">ABSENT</strong>.</li>
              <li>Teacher-specific rules like <strong className="text-foreground">Late arrivals</strong> and shift timings are strictly excluded.</li>
            </ul>
          </div>

          <button
            onClick={handleSyncBiometric}
            disabled={syncingBiometric}
            className="h-12 px-8 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm flex items-center gap-3 mx-auto disabled:opacity-50 shadow-lg transition-all"
          >
            {syncingBiometric ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {syncingBiometric ? 'Processing Biometric Punches...' : `Sync RFID Punches for ${date}`}
          </button>

          {biometricSummary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 text-left">
              <div className="p-4 rounded-2xl border border-border bg-card">
                <div className="label-eyebrow text-muted-foreground">Raw Logs</div>
                <div className="font-display font-black text-xl mt-1">{biometricSummary.totalLogs}</div>
              </div>
              <div className="p-4 rounded-2xl border border-border bg-card">
                <div className="label-eyebrow text-muted-foreground">Classes Processed</div>
                <div className="font-display font-black text-xl mt-1">{biometricSummary.classesProcessed}</div>
              </div>
              <div className="p-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10">
                <div className="label-eyebrow text-emerald-500">Marked Present</div>
                <div className="font-display font-black text-xl mt-1 text-emerald-500">{biometricSummary.totalPresent}</div>
              </div>
              <div className="p-4 rounded-2xl border border-rose-500/30 bg-rose-500/10">
                <div className="label-eyebrow text-rose-500">Marked Absent</div>
                <div className="font-display font-black text-xl mt-1 text-rose-500">{biometricSummary.totalAbsent}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
          TAB 3: CLASS ATTENDANCE REGISTER & WHATSAPP NOTIFIER
      ────────────────────────────────────────────────────────────────────────── */}
      {activeTab === 'register' && (
        <div className="space-y-5">
          {/* Class and Section Filter */}
          <div className="glass-morphism rounded-[2rem] p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <label className="label-eyebrow text-muted-foreground block text-[10px]">Select Class</label>
                <select
                  value={cls}
                  onChange={(e) => setCls(e.target.value)}
                  className="h-10 px-3 rounded-2xl border border-border bg-card text-xs font-bold"
                  data-testid="rfid-class"
                >
                  {CLASS_OPTIONS.map((c) => (
                    <option key={c} value={c}>Class {c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label-eyebrow text-muted-foreground block text-[10px]">Section</label>
                <select
                  value={sec}
                  onChange={(e) => setSec(e.target.value)}
                  className="h-10 px-3 rounded-2xl border border-border bg-card text-xs font-bold"
                  data-testid="rfid-section"
                >
                  {SECTION_OPTIONS.map((s) => (
                    <option key={s} value={s}>Section {s}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={loadRegister}
                className="self-end h-10 px-4 rounded-2xl bg-muted hover:bg-muted/80 text-xs font-bold flex items-center gap-2"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loadingRegister ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={sendBulkWhatsApp}
                disabled={absentees.length === 0}
                className="h-10 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-2 disabled:opacity-50 transition-colors"
                data-testid="rfid-bulk-wa"
              >
                <Send className="h-3.5 w-3.5" />
                Notify Absent Parents ({absentees.length})
              </button>
            </div>
          </div>

          {/* Stat Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="glass-morphism rounded-2xl p-4">
              <div className="label-eyebrow text-muted-foreground">Class Strength</div>
              <div className="font-display font-black text-2xl tracking-tighter mt-1">{registerStats.total}</div>
            </div>
            <div className="glass-morphism rounded-2xl p-4 border border-emerald-500/20 bg-emerald-500/5">
              <div className="label-eyebrow text-emerald-500">Present</div>
              <div className="font-display font-black text-2xl tracking-tighter mt-1 text-emerald-500">{registerStats.pres}</div>
            </div>
            <div className="glass-morphism rounded-2xl p-4 border border-rose-500/20 bg-rose-500/5">
              <div className="label-eyebrow text-rose-500">Absent</div>
              <div className="font-display font-black text-2xl tracking-tighter mt-1 text-rose-500">{registerStats.abs}</div>
            </div>
            <div className="glass-morphism rounded-2xl p-4 border border-indigo-500/20 bg-indigo-500/5">
              <div className="label-eyebrow text-indigo-500">Attendance Rate</div>
              <div className="font-display font-black text-2xl tracking-tighter mt-1 text-indigo-500">{registerStats.rate}%</div>
            </div>
          </div>

          {/* Student Attendance List */}
          <div className="glass-morphism rounded-[2rem] p-4 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-border text-muted-foreground label-eyebrow">
                  <th className="py-2.5 px-3">#</th>
                  <th className="py-2.5 px-3">Adm No</th>
                  <th className="py-2.5 px-3">Student Name</th>
                  <th className="py-2.5 px-3">Mapped RFID</th>
                  <th className="py-2.5 px-3">Punch Time</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {currentClassStudents.map((student, idx) => {
                  const status = (classAttendance.records || {})[student.id] || 'ABSENT';
                  const rfidLog = (classAttendance.rfidLogs || {})[student.id];

                  return (
                    <tr key={student.id} className="hover:bg-muted/30 transition-colors">
                      <td className="py-2.5 px-3 text-muted-foreground">{idx + 1}</td>
                      <td className="py-2.5 px-3 font-mono font-bold">{student.admissionNo}</td>
                      <td className="py-2.5 px-3">
                        <div className="font-bold text-foreground">{student.fullName}</div>
                        <div className="text-[10px] text-muted-foreground">Roll: {student.rollNo || '—'}</div>
                      </td>
                      <td className="py-2.5 px-3 font-mono">
                        {student.rfid || student.rfidNo ? (
                          <span className="px-2 py-0.5 rounded bg-muted font-bold">
                            {student.rfid || student.rfidNo}
                          </span>
                        ) : (
                          <span className="text-muted-foreground italic">No RFID</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-muted-foreground">
                        {rfidLog?.punchTime || '—'}
                      </td>
                      <td className="py-2.5 px-3">
                        <button
                          onClick={() => handleToggleStudentStatus(student.id, status)}
                          className={`px-3 py-1 rounded-full font-bold text-[11px] label-eyebrow transition-transform active:scale-95 ${
                            status === 'PRESENT'
                              ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/30'
                              : 'bg-rose-500/10 text-rose-600 border border-rose-500/30'
                          }`}
                        >
                          {status === 'PRESENT' ? '✓ PRESENT' : '✗ ABSENT'}
                        </button>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        {status === 'ABSENT' && (student.fatherPhone || student.phoneNumber) && (
                          <a
                            href={getWhatsAppUrl(
                              student.fatherPhone || student.phoneNumber,
                              `Dear Parent, ${student.fullName} was marked ABSENT on ${date} at St. Paul's High School.`
                            )}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 text-[11px] font-bold"
                          >
                            <MessageCircle className="h-3 w-3" /> WhatsApp
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {currentClassStudents.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-muted-foreground">
                      No students found in Class {cls}-{sec}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
          TAB 4: CSV FILE UPLOAD
      ────────────────────────────────────────────────────────────────────────── */}
      {activeTab === 'file' && (
        <div className="space-y-5">
          <label className="block glass-morphism rounded-[2rem] p-8 border-2 border-dashed border-border cursor-pointer text-center hover:border-primary/50 transition-colors">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="rfid-file"
            />
            <UploadCloud className="h-10 w-10 mx-auto text-primary" />
            <div className="mt-3 font-bold">Upload RFID Attendance CSV</div>
            <div className="label-eyebrow text-muted-foreground mt-1">
              Columns: rfid (or admissionNo), present (1/0)
            </div>
          </label>

          {fileParsed.length > 0 && (
            <div className="glass-morphism rounded-[2rem] p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="font-bold text-sm">
                  Parsed {fileParsed.length} rows from CSV file
                </div>
                <button
                  onClick={handleCommitFile}
                  disabled={committing}
                  className="h-10 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-2 disabled:opacity-50"
                  data-testid="rfid-commit"
                >
                  {committing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  {committing ? 'Saving Attendance...' : `Commit Attendance for ${date}`}
                </button>
              </div>

              <div className="overflow-x-auto max-h-[300px] thin-scrollbar">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground label-eyebrow">
                      <th className="py-2 px-3">Raw ID / RFID</th>
                      <th className="py-2 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {fileParsed.slice(0, 50).map((r, i) => (
                      <tr key={i}>
                        <td className="py-2 px-3 font-mono">{r.id}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded-full font-bold ${r.present ? 'text-emerald-500' : 'text-rose-500'}`}>
                            {r.present ? 'PRESENT' : 'ABSENT'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
